import React from "react";
import { FormControl, FormHelperText } from "@mui/material";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import type { UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import Select from "@components/Select/Select";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useTerminalServiceDocumentUpload } from "@features/terminal-services/hooks/useTerminalServiceDocumentUpload";
import {
  FottServiceType,
  type FottServiceType as FottServiceTypeValue,
  type TerminalServiceDocument,
} from "@services/terminal-services/types";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

const LANG_BASE = "terminal-services.approve";
const TEST_ID_BASE = "terminal-service-approve-drawer";
const OPERATOR_NAME_MAX_CHARS = 100;
const NOTES_MAX_CHARS = 500;
const ALLOWED_FORMATS = ["pdf", "jpg", "jpeg", "png"];
const VALIDATION_MESSAGE =
  "File must be under 100MB and in one of the following formats: PDF, JPG, JPEG, PNG.";
const JOB_TYPE_OPTIONS = [
  { value: "Others", labelKey: `${LANG_BASE}.job-types.others` },
  { value: "PoF", labelKey: `${LANG_BASE}.job-types.pof` },
] as const;

type ApprovalForm = Record<string, string | File[]>;
const NOTES_FIELD_NAME = "notesToCustomer";
const OPERATOR_NAME_FIELD_NAME = "operatorName";

export type TerminalServiceApproveDraft = {
  jobType: string;
  operatorName: string;
  notesToCustomer: string;
};

type Props = {
  isOpen: boolean;
  requestId?: string | null;
  serviceType: FottServiceTypeValue;
  draft: TerminalServiceApproveDraft;
  onDraftChange: (draft: TerminalServiceApproveDraft) => void;
  onClose: (options?: { resetDraft?: boolean }) => void;
  onApprove: (payload: {
    jobType: string;
    operatorName?: string;
    notesToCustomer?: string;
    bookingConfirmation: TerminalServiceDocument;
  }) => void;
  isSubmitting?: boolean;
};

const getDocument = (entry?: UploadEntry): TerminalServiceDocument | null => {
  if (!entry || entry instanceof File || !("url" in entry) || typeof entry.url !== "string") {
    return null;
  }
  return {
    documentUrl: entry.url,
    documentName: typeof entry.name === "string" ? entry.name : undefined,
    documentType: typeof entry.type === "string" ? entry.type : undefined,
  };
};

const ApproveTerminalServiceDrawer = ({
  isOpen,
  requestId,
  serviceType,
  draft,
  onDraftChange,
  onClose,
  onApprove,
  isSubmitting = false,
}: Props) => {
  const { t } = useTranslation();
  const isGantry = serviceType === FottServiceType.GANTRY_CRANE_SERVICE;
  const storeKey = `terminalServiceApprovalBooking-${requestId ?? "unknown"}`;
  const form = useForm<ApprovalForm>({
    defaultValues: {
      [NOTES_FIELD_NAME]: draft.notesToCustomer,
      [OPERATOR_NAME_FIELD_NAME]: draft.operatorName,
      [storeKey]: [],
    },
  });
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const profile = useFileUploadProfiles().sideDrawerApprovalFile;
  const { uploadEntries, setUploadEntries, handleUpload, resetUpload, isUploading } =
    useTerminalServiceDocumentUpload({
      storeKey,
      certificateType: "FOTT_SERVICE_BOOKING_CONFIRMATION",
      fallbackFileNamePrefix: "booking-confirmation",
      errorNotification: {
        titleKey: `${LANG_BASE}.notifications.upload-error-title`,
        descriptionKey: `${LANG_BASE}.notifications.upload-error-description`,
      },
    });

  const title = t(`${LANG_BASE}.title.${serviceType}`, {
    defaultValue: `Approve ${t(`terminal-services.fott-services.service-type.${serviceType}`)}`,
  });

  const reset = React.useCallback(() => {
    onDraftChange({ jobType: "", operatorName: "", notesToCustomer: "" });
    setErrors({});
    form.reset({ [NOTES_FIELD_NAME]: "", [OPERATOR_NAME_FIELD_NAME]: "", [storeKey]: [] });
    resetUpload();
  }, [form, onDraftChange, resetUpload, storeKey]);

  const cancel = () => {
    reset();
    onClose({ resetDraft: true });
  };

  const submit = () => {
    const bookingConfirmation = getDocument(uploadEntries[0]);
    const notesToCustomer = form.getValues(NOTES_FIELD_NAME);
    const operatorName = form.getValues(OPERATOR_NAME_FIELD_NAME);
    const normalizedNotes = typeof notesToCustomer === "string" ? notesToCustomer : "";
    const normalizedOperatorName = typeof operatorName === "string" ? operatorName : "";
    const nextErrors: Record<string, string> = {};
    if (!draft.jobType.trim())
      nextErrors.jobType = t("form-validation.required", { label: "Job Type" });
    if (isGantry && !normalizedOperatorName.trim()) {
      nextErrors.operatorName = t("form-validation.required", { label: "Operator Name" });
      form.setError(OPERATOR_NAME_FIELD_NAME, {
        type: "required",
        message: nextErrors.operatorName,
      });
    } else if (isGantry && normalizedOperatorName.trim().length > OPERATOR_NAME_MAX_CHARS) {
      nextErrors.operatorName = t("form-validation.max-length", {
        label: "Operator Name",
        max: OPERATOR_NAME_MAX_CHARS,
      });
      form.setError(OPERATOR_NAME_FIELD_NAME, {
        type: "maxLength",
        message: nextErrors.operatorName,
      });
    }
    if (normalizedNotes.trim().length > NOTES_MAX_CHARS) {
      nextErrors.notesToCustomer = t("form-validation.max-length", {
        label: "Notes to the Customer",
        max: NOTES_MAX_CHARS,
      });
    }
    if (!bookingConfirmation) {
      nextErrors.bookingConfirmation = t("form-validation.required", {
        label: "Booking Confirmation",
      });
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length || !bookingConfirmation) return;

    onApprove({
      jobType: draft.jobType,
      operatorName: isGantry ? normalizedOperatorName.trim() : undefined,
      notesToCustomer: normalizedNotes.trim() || undefined,
      bookingConfirmation,
    });
  };

  if (!isOpen) return null;

  const update = (patch: Partial<TerminalServiceApproveDraft>) =>
    onDraftChange({ ...draft, ...patch });

  return (
    <div data-testid={TEST_ID_BASE} className="w-[400px] max-w-[100vw]">
      <SideDrawer
        isOpen={isOpen}
        title={title}
        description={t(`${LANG_BASE}.description`)}
        onClose={() => onClose({ resetDraft: false })}
        onCancel={cancel}
        submitLabel={t(`${LANG_BASE}.submit`)}
        cancelLabel={t("common.cancel")}
        onSubmit={submit}
        isSubmitting={isSubmitting}
        isSubmitDisabled={isUploading}
      >
        <div className="flex flex-col gap-4 w-[400px] max-w-[100vw] p-6">
          <FormControl fullWidth error={Boolean(errors.jobType)}>
            <CustomFormLabel sx={{ mb: 1 }}>{t(`${LANG_BASE}.fields.job-type`)}</CustomFormLabel>
            <Select
              testId={`${TEST_ID_BASE}-job-type`}
              value={draft.jobType}
              error={Boolean(errors.jobType)}
              placeholder={t(`${LANG_BASE}.fields.job-type-placeholder`)}
              options={JOB_TYPE_OPTIONS.map(({ value, labelKey }) => ({
                value,
                label: t(labelKey),
                testId: `${TEST_ID_BASE}-job-type-${value}`,
              }))}
              onChange={(value) => {
                update({ jobType: value });
                setErrors((current) => ({ ...current, jobType: "" }));
              }}
            />
            {errors.jobType && <FormHelperText sx={{ ml: 0 }}>{errors.jobType}</FormHelperText>}
          </FormControl>

          <FormProvider {...form}>
            {isGantry && (
              <FormItem<ApprovalForm>
                name={OPERATOR_NAME_FIELD_NAME}
                type="text"
                label={t(`${LANG_BASE}.fields.operator-name`)}
                validationLabel={t(`${LANG_BASE}.fields.operator-name`)}
                testId={`${TEST_ID_BASE}-operator-name`}
                maxChars={OPERATOR_NAME_MAX_CHARS}
                onFieldChange={(_, value) => {
                  if (typeof value === "string") {
                    update({ operatorName: value });
                  }
                }}
              />
            )}
            <FormItem<ApprovalForm>
              name={NOTES_FIELD_NAME}
              type="textarea"
              label={t(`${LANG_BASE}.fields.notes-to-customer`)}
              validationLabel={t(`${LANG_BASE}.fields.notes-to-customer`)}
              testId={`${TEST_ID_BASE}-notes`}
              maxChars={NOTES_MAX_CHARS}
              showCharCounter
              minRows={4}
              onFieldChange={(_, value) => {
                if (typeof value === "string") {
                  update({ notesToCustomer: value });
                }
              }}
            />
            <div className="flex flex-col gap-2">
              <FormItem<ApprovalForm>
                name={storeKey}
                type="file"
                maxFiles={1}
                label={t(`${LANG_BASE}.fields.booking-confirmation`)}
                testId={`${TEST_ID_BASE}-booking-confirmation-upload`}
                uploadConditions={{ ...profile, format: ALLOWED_FORMATS, hideErrors: true }}
                allowedFileExtensions={ALLOWED_FORMATS.map((format) => `.${format}`)}
                uploadDescription={VALIDATION_MESSAGE}
                files={uploadEntries}
                setFiles={setUploadEntries}
                onUpload={(_, files) => {
                  setErrors((current) => ({ ...current, bookingConfirmation: "" }));
                  handleUpload(files);
                }}
                onReject={() =>
                  setErrors((current) => ({ ...current, bookingConfirmation: VALIDATION_MESSAGE }))
                }
                onRemoveFile={() => resetUpload()}
                disabled={isSubmitting || isUploading}
                showErrorMsg={false}
              />
              {errors.bookingConfirmation && (
                <FormHelperText
                  error
                  sx={{ m: 0 }}
                  data-testid={`${TEST_ID_BASE}-booking-confirmation-error`}
                >
                  {errors.bookingConfirmation}
                </FormHelperText>
              )}
              {uploadEntries[0] && (
                <SingleFile
                  file={uploadEntries[0]}
                  fieldName={storeKey}
                  currentFileIndex={0}
                  setFiles={resetUpload}
                  removeFn={resetUpload}
                  isLoading={isUploading && uploadEntries[0] instanceof File}
                />
              )}
            </div>
          </FormProvider>
        </div>
      </SideDrawer>
    </div>
  );
};

export default ApproveTerminalServiceDrawer;
